import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { Product } from '@core/models';
import { StoreConfigService } from './store-config.service';
import { CategoryService } from './category.service';
import { mapRawProduct, ITEM_TYPE_TO_BACKEND } from './product.mapper';
import { isSoldOut } from '@core/utils/stock';
import { swrGet } from '@core/utils/swr-get';
import { environment } from '@environments/environment';

/** Lote para filtros que store-gtw no resuelve (precio, stock). */
const CLIENT_FILTER_POOL = 200;

export interface ProductFilters {
  categorySlug?: string;
  brandIds?: string[];
  minPrice?: number;
  maxPrice?: number;
  inStockOnly?: boolean;
  itemType?: 'PRODUCT' | 'SERVICE' | 'KIT';
  q?: string;
}

/** Lo que ve el cliente al entrar a un listado: todo el catálogo; los agotados van al final y "Solo con stock" los oculta. */
export const DEFAULT_PRODUCT_FILTERS: ProductFilters = { inStockOnly: false };

export type ProductSort = 'relevance' | 'price-asc' | 'price-desc' | 'popularity' | 'newest';

export interface ProductPage {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Consume el catálogo real vía `store-gtw` (`GET /store/:slug/products`).
 * `store-gtw` ya filtra por `publishedOnline` + `active`: solo se ve lo que
 * el negocio marcó "Visible en tienda" desde Inventario en faisen-admin-web.
 */
@Injectable({ providedIn: 'root' })
export class ProductService {
  private http = inject(HttpClient);
  private storeConfig = inject(StoreConfigService);
  private categoryService = inject(CategoryService);

  private get baseUrl(): string {
    return `${environment.apiUrl}/store/${this.storeConfig.currentSlug()}/products`;
  }

  getAll(filters: ProductFilters = {}, sort: ProductSort = 'relevance', page = 1, pageSize = 12): Observable<ProductPage> {
    const categoryId$ = filters.categorySlug
      ? this.categoryService.resolveIdBySlug(filters.categorySlug)
      : of(undefined);

    // ponytail: store-gtw no filtra por precio ni stock. Con esos filtros se trae un lote
    // (CLIENT_FILTER_POOL) y se pagina aquí: antes se filtraba solo la página actual y el
    // total/paginación del servidor no coincidían con lo mostrado. Mover a backend si crece.
    // `inStockOnly === false` (vista completa) también va por aquí: para poner los agotados al final de TODO el listado hay que ordenar el lote entero, no solo cada página.
    const clientSide = filters.minPrice != null || filters.maxPrice != null || filters.inStockOnly !== undefined;

    return categoryId$.pipe(
      switchMap((categoryId) => {
        let params = new HttpParams()
          .set('page', clientSide ? 1 : page)
          .set('limit', clientSide ? CLIENT_FILTER_POOL : pageSize);
        if (categoryId) params = params.set('category', categoryId);
        if (filters.brandIds?.[0]) params = params.set('brand', filters.brandIds[0]);
        if (filters.itemType) params = params.set('type', ITEM_TYPE_TO_BACKEND[filters.itemType]);
        if (filters.q?.trim()) params = params.set('q', filters.q.trim());

        return swrGet<any>(this.http, this.baseUrl, params).pipe(
          map((res) => {
            let items: Product[] = (res.data || []).map(mapRawProduct);
            if (!clientSide) {
              return {
                items: this.applySort(items, sort),
                total: res.total ?? items.length,
                page: res.page ?? page,
                pageSize: res.limit ?? pageSize,
                totalPages: res.totalPages ?? 1,
              };
            }
            if (filters.minPrice != null) items = items.filter((p) => p.price >= filters.minPrice!);
            if (filters.maxPrice != null) items = items.filter((p) => p.price <= filters.maxPrice!);
            if (filters.inStockOnly) items = items.filter((p) => !isSoldOut(p));
            items = this.applySort(items, sort);
            const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
            const current = Math.min(page, totalPages);
            return {
              items: items.slice((current - 1) * pageSize, current * pageSize),
              total: items.length,
              page: current,
              pageSize,
              totalPages,
            };
          }),
        );
      }),
    );
  }

  /**
   * Cuántos hay publicados de cada tipo (limit=1, solo se lee `total`). `null` = no se pudo
   * saber: el header muestra la opción antes que esconder navegación por un fallo puntual.
   */
  countByType(): Observable<Record<'PRODUCT' | 'SERVICE' | 'KIT', number | null>> {
    const count = (type: 'PRODUCT' | 'SERVICE' | 'KIT') =>
      this.http
        .get<any>(this.baseUrl, { params: new HttpParams().set('limit', 1).set('type', ITEM_TYPE_TO_BACKEND[type]) })
        .pipe(
          map((res) => (typeof res?.total === 'number' ? res.total : (res?.data || []).length)),
          catchError(() => of(null)),
        );
    return forkJoin({ PRODUCT: count('PRODUCT'), SERVICE: count('SERVICE'), KIT: count('KIT') });
  }

  getBySlug(slug: string): Observable<Product | undefined> {
    return swrGet<any>(this.http, `${environment.apiUrl}/store/${this.storeConfig.currentSlug()}/products/${slug}`)
      .pipe(map((raw) => (raw ? mapRawProduct(raw) : undefined)));
  }

  getFeatured(limit = 8): Observable<Product[]> {
    return this.getAll({ itemType: 'PRODUCT' }, 'relevance', 1, limit).pipe(map((p) => p.items));
  }

  getPromoKits(limit = 8): Observable<Product[]> {
    return this.getAll({ itemType: 'KIT' }, 'relevance', 1, limit).pipe(map((p) => p.items));
  }

  getServices(limit = 8): Observable<Product[]> {
    return this.getAll({ itemType: 'SERVICE' }, 'relevance', 1, limit).pipe(map((p) => p.items));
  }

  getBestSellers(limit = 8): Observable<Product[]> {
    // ponytail: sin agregación real de ventas por pedido todavía; usa catálogo activo como aproximación.
    return this.getAll({ itemType: 'PRODUCT' }, 'relevance', 1, limit).pipe(map((p) => p.items));
  }

  getOnSale(_limit = 8): Observable<Product[]> {
    // Sin sistema de descuentos/promociones en el backend todavía (ver análisis E-Commerce).
    return of([]);
  }

  getNewArrivals(limit = 8): Observable<Product[]> {
    return this.getAll({ itemType: 'PRODUCT' }, 'newest', 1, limit).pipe(map((p) => p.items));
  }

  getRelated(product: Product, limit = 4): Observable<Product[]> {
    return this.getAll({}, 'relevance', 1, limit + 1).pipe(
      map((p) => p.items.filter((x) => x.id !== product.id).slice(0, limit)),
    );
  }

  getByIds(ids: string[]): Observable<Product[]> {
    // ponytail: sin endpoint público de batch-by-id; usado hoy solo por favoritos locales.
    // Se resuelve filtrando el catálogo visible; si crece mucho, pedir endpoint dedicado.
    if (!ids.length) return of([]);
    return this.getAll({}, 'relevance', 1, 200).pipe(
      map((p) => p.items.filter((item) => ids.includes(item.id))),
    );
  }

  /** Ordena según `sort` y deja los agotados al final (conservando el orden dentro de cada grupo). */
  private applySort(items: Product[], sort: ProductSort): Product[] {
    const sorted = this.sortBy(items, sort);
    return [...sorted.filter((p) => !isSoldOut(p)), ...sorted.filter((p) => isSoldOut(p))];
  }

  private sortBy(items: Product[], sort: ProductSort): Product[] {
    const sorted = [...items];
    switch (sort) {
      case 'price-asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sorted.sort((a, b) => b.price - a.price);
      case 'newest':
        return sorted.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
      default:
        return sorted;
    }
  }
}
